/**
 * VideoSDK's own tally of the identity-verification APIs this journey consumed,
 * captured when the journey ended and stored on the journey record.
 *
 * Purely presentational — the data arrives with journey/fetch, so there is no
 * request here. It sits directly above the identity call log on purpose: these
 * counts come from VideoSDK while the log rows are written by our own recorder,
 * so seeing them together makes a disagreement between the two visible, which
 * is the signal that some log writes were lost.
 */

// Response keys as VideoSDK returns them, in pipeline order.
const USAGE_FIELDS = [
  { key: "findFaces", label: "Face Detection" },
  { key: "spoofDetection", label: "Spoof Detection" },
  { key: "faceMatch", label: "Face Match" },
];

export default function JourneyIdentityUsage({ usage, isLoading }) {
  const parsed = parseUsage(usage);

  return (
    <div className="mt-4 border-2 border-gray-300 flex flex-col">
      <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
        Identity API Consumption
      </p>

      {isLoading ? (
        <div className="w-full py-3 px-3">
          <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
        </div>
      ) : !parsed ? (
        <div className="flex items-center justify-center py-3 px-3 text-base">
          No Data Available
        </div>
      ) : (
        <div className="grid grid-cols-3">
          {USAGE_FIELDS.map(({ key, label }) => (
            <div
              key={key}
              className="flex flex-col px-3 py-2 border border-gray-300"
            >
              <p className="text-sm text-black-150 mb-0">{label}</p>
              <p className="text-base font-semibold text-black-150 mt-2 mb-0">
                {/* A count of 0 is a real answer, so test for presence rather
                    than truthiness — 0 must not render as an em dash. */}
                {typeof parsed[key] === "number" ? parsed[key] : "-"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The column stores JSON as text, and the Oracle dialect may hand it back
 * either already parsed or as the raw string. Accept both; anything else is
 * treated as absent rather than rendered as "[object Object]".
 */
function parseUsage(usage) {
  if (!usage) return null;
  if (typeof usage === "object") return usage;
  if (typeof usage === "string") {
    try {
      const parsed = JSON.parse(usage);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
      return null;
    }
  }
  return null;
}
